import { useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, PlusIcon } from '@heroicons/react/24/outline';

export interface VenueOption {
  id: string;
  name: string;
  address: string;
}

export type VenueSelection =
  | { kind: 'existing'; venue: VenueOption }
  | { kind: 'new'; name: string }
  | null;

interface VenueComboboxProps {
  venues: VenueOption[];
  value: VenueSelection;
  onChange: (selection: VenueSelection) => void;
}

function venueSelectionsEqual(a: VenueSelection, b: VenueSelection): boolean {
  if (!a || !b) {
    return a === b;
  }
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === 'existing' && b.kind === 'existing') {
    return a.venue.id === b.venue.id;
  }
  if (a.kind === 'new' && b.kind === 'new') {
    return a.name === b.name;
  }
  return false;
}

function displayVenueSelection(selection: VenueSelection): string {
  if (!selection) {
    return '';
  }
  return selection.kind === 'existing'
    ? `${selection.venue.name} - ${selection.venue.address}`
    : selection.name;
}

const VenueCombobox: React.FC<VenueComboboxProps> = ({ venues, value, onChange }) => {
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();
  const filteredVenues = trimmedQuery
    ? venues.filter(
        (venue) =>
          venue.name.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
          venue.address.toLowerCase().includes(trimmedQuery.toLowerCase())
      )
    : venues;
  const canCreate =
    trimmedQuery.length > 0 &&
    !venues.some((venue) => venue.name.toLowerCase() === trimmedQuery.toLowerCase());

  return (
    <Combobox value={value} onChange={onChange} by={venueSelectionsEqual} nullable>
      <div className="relative">
        <Combobox.Input
          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800"
          placeholder="Search venues or type a new venue name..."
          displayValue={displayVenueSelection}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </Combobox.Button>
        {(filteredVenues.length > 0 || canCreate) && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white dark:bg-gray-800 py-1 shadow-lg border border-gray-200 dark:border-gray-700 focus:outline-none">
            {canCreate && (
              <Combobox.Option
                value={{ kind: 'new', name: trimmedQuery } as VenueSelection}
                className={({ active }) =>
                  `cursor-pointer select-none px-3 py-2 flex items-center text-primary-600 dark:text-primary-400 ${
                    active ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`
                }
              >
                <PlusIcon className="h-4 w-4 mr-2 flex-shrink-0" aria-hidden="true" />
                Add &quot;{trimmedQuery}&quot; as a new venue
              </Combobox.Option>
            )}
            {filteredVenues.map((venue) => (
              <Combobox.Option
                key={venue.id}
                value={{ kind: 'existing', venue } as VenueSelection}
                className={({ active }) =>
                  `cursor-pointer select-none px-3 py-2 ${
                    active ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`
                }
              >
                {({ selected }) => (
                  <span className="flex items-center justify-between">
                    <span className={selected ? 'font-medium' : ''}>
                      {venue.name}
                      <span className="text-gray-500 dark:text-gray-400"> - {venue.address}</span>
                    </span>
                    {selected && (
                      <CheckIcon className="h-4 w-4 text-primary-600 flex-shrink-0" aria-hidden="true" />
                    )}
                  </span>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

export default VenueCombobox;
